/*
 * ===========================================================================
 * [StrongGate Limited] [http://www.stronggate.com]
 * Copyright (c) 2023. All rights Reserved.
 * Unauthorized access, use, dissemination, distribution, reproduction of this
 * file, or a portion of, in any form is prohibited and unlawful. If you receive
 * this file, and you are not an authorized company employee, or you believe you
 * have received this file in error, please notify the sender (or originator)
 * and promptly delete this file.
 *
 * Project:
 * File: StringUtil.ts
 * Created by: Kevin
 * Create Date: 2023-07-10 09:30
 * Version Number: 1.0
 * Version Description:
 *
 * =============================================================================
 */

export default class StringUtil {

    private constructor() {
    }

    public static readonly S_Name_Length: number = 255;                 // name max length
    public static readonly S_Desc_Length: number = 500;                 // description max length
    public static readonly S_Greater_Then_Char_Code_At: number = 127;
    public static readonly S_Equal_To_Char_Code_At: number = 94;

    public static readonly S_Query_Wait_Time = 300;                     // waiting time for the query

    /**
     * escapeHtml escapes the five HTML special characters so a plain-text
     * value can be embedded inside an HTML fragment (e.g. an i18n message
     * rendered through v-safe-html) without being parsed as markup.
     */
    public static escapeHtml(value: string | null | undefined): string {
        if (!value) {
            return '';
        }
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    /**
     * S_UUID_NIL represents a nil UUID (Universally Unique Identifier).
     * It is a unique identifier that is essentially a string of all zeros,
     * conforming to the standard UUID format.
     *
     * Typically used as a placeholder or default value for systems requiring
     * a UUID but where no specific value is yet assigned.
     *
     * Format: "00000000-0000-0000-0000-000000000000"
     */
    public static readonly S_UUID_NIL = "00000000-0000-0000-0000-000000000000"

    /**
     * Reduce multiple spaces to one space
     * Example: "a  b   c" -> "a b c"
     * @param s
     */
    public static reduceSpaces(s: string): string {
        return s.replaceAll(/ {2,}/g, ' ');
    }

    public static countCharacterOccurrences(str: string, char: string): number {
        return str.split(char).length - 1;
    }

    public static removeAllDoubleBytes(str: string): string {
        let result = '';
        for (let i = 0; i < str.length; i++) {
            const charCode = str.charCodeAt(i);
            if (charCode >= 0 && charCode <= 255) {
                result += str.charAt(i);
            }
        }
        return result;
    }

    public static trimEmptyLines(str: string): string {
        const lines = str.split('\n');
        const trimmedLines = lines.filter(line => line.trim() !== '');
        return trimmedLines.join('\n');
    }

    public static copyToClipboard = async (text: string): Promise<boolean> => {
        try {
            // preferential: Clipboard API
            if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                await navigator.clipboard.writeText(text)
                return true
            }

            // Downgrading plan (compatible with HTTP and older browsers)
            const textarea = document.createElement('textarea')
            textarea.value = text
            textarea.style.position = 'fixed'
            textarea.style.left = '-999999px'
            textarea.style.top = '-999999px'
            document.body.appendChild(textarea)
            textarea.focus()
            textarea.select()

            const successful = document.execCommand('copy')
            document.body.removeChild(textarea)
            return successful
        } catch (err) {
            console.error('Copy failed, cause: ', err)
            return false
        }
    }
}